import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GetRedsysPublicStatus } from '../../Backend/ApiCalls';
import { formatCents } from '../../utils/formatMoney';
import './PagoEncuestaResultComponent.css';

// amountCents SIEMPRE en céntimos, como lo devuelve el backend.
const formatMoney = (amountCents) => formatCents(amountCents);

const providerLabel = (provider) =>
  provider === 'redsys_bizum' ? 'Bizum'
    : provider === 'redsys_xpay' ? 'Apple Pay / Google Pay'
      : 'Tarjeta';

// Enmascara el pedido para no mostrarlo completo en pantalla (ej. capturas
// compartidas). No es un dato secreto -ya viaja en la URL de retorno-, pero
// no hace falta exponerlo entero como si fuera un justificante oficial.
export const maskOrder = (order) => {
  const value = String(order || '');
  if (value.length <= 4) return value;
  return `${value.slice(0, 4)}••••${value.slice(-3)}`;
};

// El estado real SIEMPRE lo decide el backend, nunca el hecho de haber
// llegado a esta pantalla ni el modo ok/ko de la URL. Redsys puede
// redirigir a "ok" y que el backend, tras validar firma/importe, todavía no
// haya confirmado nada (pending) o lo haya rechazado.
export const STATUS_PRESENTATION = {
  paid: { headline: 'Pago confirmado', tone: 'ok', icon: '✓' },
  pending: { headline: 'Estamos verificando el pago', tone: 'pending', icon: '…' },
  failed: { headline: 'No se ha podido completar el pago', tone: 'error', icon: '✕' },
  cancelled: { headline: 'Has cancelado el pago', tone: 'error', icon: '✕' },
};

const isLikelyNativeApp = () =>
  Boolean(window?.Capacitor?.isNativePlatform && window.Capacitor.isNativePlatform());

/**
 * Pantalla de resultado del pago (/pago-ok, /pago-ko). A propósito NO
 * depende de que exista sesión: puede abrirse en un navegador externo sin
 * localStorage/sessionStorage de la app. Se autentica frente al backend con
 * el receiptToken que viaja en la propia URL de retorno (ver
 * makeReceiptToken en el backend), nunca con el token de sesión.
 */
export const PagoEncuestaResultComponent = ({ mode }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [statusData, setStatusData] = useState(null);
  const [error, setError] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);

  const order = searchParams.get('order');
  const receiptToken = searchParams.get('receiptToken');
  const cameFromApp = searchParams.get('source') === 'app';

  const fetchStatus = useCallback(async () => {
    if (!order || !receiptToken) {
      setError('No se ha recibido ninguna referencia de pago válida.');
      return;
    }

    setIsChecking(true);
    setError('');
    try {
      const { response, data } = await GetRedsysPublicStatus(order, receiptToken);
      if (!response.ok) {
        setError(data?.Error || 'No se ha podido consultar el estado del pago.');
        return;
      }

      setStatusData(data);
    } catch (fetchError) {
      console.error('Error consultando el estado del pago:', fetchError);
      setError('Ha ocurrido un problema al consultar el pago. Comprueba tu conexión e inténtalo de nuevo.');
    } finally {
      setIsChecking(false);
    }
  }, [order, receiptToken]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Limpiamos la operación pendiente guardada localmente en cuanto el
  // backend confirme un estado final, para que el aviso "Verificando pago"
  // no siga apareciendo dentro de la app.
  useEffect(() => {
    const status = statusData?.status;
    if (status && status !== 'pending') {
      try {
        localStorage.removeItem('pending_redsys_payment');
      } catch {
        // noop
      }
    }
  }, [statusData]);

  const intentStatus = statusData?.status;
  const presentation = STATUS_PRESENTATION[intentStatus] || {
    headline: error ? 'Ha ocurrido un problema al consultar el pago' : 'No se ha podido comprobar todavía',
    tone: 'unknown',
    icon: '?',
  };

  const items = Array.isArray(statusData?.items) ? statusData.items : [];
  const allSurveyAnswers = items.flatMap((item) =>
    (item.surveyAnswers || []).map((answer, index) => ({
      ...answer,
      key: `${item.beneficiaryName}-${item.beneficiarySurname}-${index}`,
      beneficiaryLabel: `${item.beneficiaryName || ''} ${item.beneficiarySurname || ''}`.trim(),
    }))
  );

  // El botón "Volver a la app" solo tiene sentido si esta página se abrió
  // desde el navegador externo lanzado por la app (source=app) y estamos
  // efectivamente en un navegador móvil, no dentro de la propia WebView de
  // Capacitor (que no debería llegar a cargar esta página directamente).
  // IMPORTANTE (ver informe, Parte 14/21/22): hoy el deep link
  // fallaplazarodrigo://payment/... solo está activo en iOS; en Android
  // sigue desactivado hasta una futura actualización de la app, así que en
  // Android este botón puede no hacer nada — por eso el mensaje de abajo no
  // asegura que "vuelve automáticamente", solo lo intenta.
  const showAppReturnButton = cameFromApp && !isLikelyNativeApp() && order;

  const handleReturnToApp = () => {
    if (!order) return;
    const path = mode === 'ok' ? 'ok' : 'ko';
    // Solo un identificador de operación en el deep link, nada sensible.
    window.location.href = `fallaplazarodrigo://payment/${path}?order=${encodeURIComponent(order)}`;
  };

  return (
    <div className="pago-resultado-standalone">
      <div className="pago-resultado-brand">Falla Plaza Rodrigo</div>

      <div className="encuesta-component pago-resultado-page">
        <div className="recipient">
          <div className={`pago-resultado-card tone-${presentation.tone}`}>
            <div className="pago-resultado-icon" aria-hidden="true">{presentation.icon}</div>
            <h1 className="pago-resultado-headline">{presentation.headline}</h1>

            {intentStatus === 'pending' && (
              <p className="pago-resultado-sub">
                Puede tardar unos segundos. No hace falta que hagas nada más: si sigue pendiente,
                pulsa "Consultar estado del pago" en un momento.
              </p>
            )}

            {intentStatus === 'failed' && (
              <p className="pago-resultado-sub payment-info-note">
                Tu inscripción y tus respuestas a la encuesta no se han perdido. Puedes volver a
                intentar el pago online más tarde o pagar presencialmente en el casal dentro del
                plazo indicado.
              </p>
            )}

            {error && !statusData && <p className="pago-resultado-sub pago-resultado-error">{error}</p>}

            {statusData && (
              <div className="pago-resultado-summary">
                <div className="pago-resultado-row">
                  <span>Concepto</span>
                  <strong>{statusData.conceptLabel || statusData.pollTitle || '—'}</strong>
                </div>
                <div className="pago-resultado-row">
                  <span>Importe</span>
                  <strong>{formatMoney(statusData.totalAmountCents)}</strong>
                </div>
                <div className="pago-resultado-row">
                  <span>Método</span>
                  <strong>{providerLabel(statusData.provider)}</strong>
                </div>
                {order && (
                  <div className="pago-resultado-row">
                    <span>Referencia</span>
                    <strong>{maskOrder(order)}</strong>
                  </div>
                )}
              </div>
            )}

            <div className="pago-resultado-actions">
              <button className="pago-resultado-btn primary" onClick={() => navigate('/contestar-encuesta')}>
                Volver a Falla Plaza Rodrigo
              </button>
              <button
                className="pago-resultado-btn secondary"
                onClick={fetchStatus}
                disabled={isChecking}
              >
                {isChecking ? 'Consultando…' : 'Consultar estado del pago'}
              </button>
              {showAppReturnButton && (
                <button className="pago-resultado-btn secondary" onClick={handleReturnToApp}>
                  Volver a la app
                </button>
              )}
            </div>

            {cameFromApp && (
              <p className="pago-resultado-hint">
                Si el botón "Volver a la app" no funciona, puedes regresar manualmente: cierra esta
                pestaña del navegador y abre de nuevo Falla Plaza Rodrigo. Esta pantalla ya
                muestra tu recibo completo aunque no vuelvas a la app.
              </p>
            )}
          </div>

          {items.length > 0 && (
            <div className="survey-responses-container">
              <h3 className="survey-responses-title">Beneficiarios</h3>
              <div className="history-list">
                {items.map((item, index) => (
                  <div key={`${item.beneficiaryName}-${item.beneficiarySurname}-${index}`} className="history-card">
                    <div className="history-card-top">
                      <span className="history-card-name">
                        {`${item.beneficiaryName || ''} ${item.beneficiarySurname || ''}`.trim() || 'Beneficiario'}
                      </span>
                      <strong className="history-card-amount">{formatMoney(item.amountCents)}</strong>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {allSurveyAnswers.length > 0 && (
            <div className="survey-responses-container">
              <h3 className="survey-responses-title">Respuestas incluidas en este pago</h3>
              <div className="user-responses">
                {allSurveyAnswers.map((answer) => (
                  <div key={answer.key} className="response-item">
                    <p><strong>Beneficiario:</strong> {answer.beneficiaryLabel}</p>
                    <p><strong>Pregunta:</strong> {answer.questionText}</p>
                    <p><strong>Respuesta:</strong> {answer.response}</p>
                  </div>
                ))}
              </div>
              <p className="payment-info-note">
                Guarda esta pantalla si necesitas justificar el pago o revisar alguna incidencia con la inscripción.
              </p>
            </div>
          )}

          {statusData && (
            <button
              type="button"
              className="pago-resultado-details-toggle"
              onClick={() => setShowTechnicalDetails((prev) => !prev)}
              aria-expanded={showTechnicalDetails}
            >
              {showTechnicalDetails ? 'Ocultar detalles técnicos' : 'Ver detalles técnicos'}
            </button>
          )}
          {showTechnicalDetails && statusData && (
            <div className="pago-resultado-technical">
              <p>Estado interno: {statusData.status}</p>
              <p>Pedido: {statusData.orderRef ? maskOrder(statusData.orderRef) : '—'}</p>
              <p>Proveedor: {statusData.provider}</p>
              <p>Pagador: {statusData.payerName || '—'}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
